import React, {useEffect, useState} from "react";

import './ChangePackageView.css'
import {SelectableTable} from "../../common/table/SelectableTable";
import {StandardButton} from "../../common/button/standard-button/StandardButton";
import {createChangePackage, deleteChangePackage, updateChangePackage} from "../../../services/ChangePackageService";
import {
    findChangeIdeasByCriteria,
    updateChangeIdea,
    viewChangeIdea,
    viewChangeIdeasForPackage
} from "../../../services/ChangeIdeaService";
import {ConfirmDeleteModal} from "../../common/confirmation-modal/ConfimDeleteModal";
import {convertDateToUTC} from "../../../util/DateUtil";

export const ChangePackageView = ({context, setContext, navigateTo, showToaster, navigateBack, setSpinnerText}) => {

    const [searchResults, setSearchResults] = useState([]);
    const [ideaListForPackage, setIdeaListForPackage] = useState([]);
    const [noResultsMessage, setNoResultsMessage] = useState(null);
    const [packageName, setPackageName] = useState("");
    const [extRefToBeDeleted, setExtRefToBeDeleted] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState(null);

    useEffect(() => {
        if (context.captureNewPackage) {
            setNoResultsMessage("No Change Ideas Selected");
            setSearchResults([]);
            setPackageName("");
            if (context.addChangeIdeasToPackage) {
                setPackageName(context.changePackageToCreate.name);
                fetchChangeIdeasByCriteria();
            }
        } else {
            if (context.addChangeIdeasToPackage) {
                setPackageName(context.selectedChangePackage.name);
                fetchChangeIdeasForPackage();
            }
        }
    }, [context]);

    const fetchChangeIdeasByCriteria = async () => {
        setSpinnerText("Retrieving Ideas...");
        const [data, error] = await findChangeIdeasByCriteria(context.username, {});
        if (data) {
            if (data.responseStatus === "Success") {
                if (context.addChangeIdeasToPackage && context.addClick) {
                    mapAddClickSearchResults(data);
                } else {
                    mapSearchResults(data);
                }
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const mapAddClickSearchResults = (data) => {
        let mappedChangeIdeaList = data.changeIdeaSearchTableItems.map((changeIdea) => {
            let copyOfSearchResults = JSON.parse(JSON.stringify(context.ideaListForPackage));
            let isIncluded = false;
            copyOfSearchResults.forEach((packageIdea) => {
                if (packageIdea.changeIdeaExternalReference === changeIdea.changeIdeaExternalReference) {
                    isIncluded = true;
                }
            });
            return {...changeIdea, includeInPackage: isIncluded}
        });
        setSearchResults(mappedChangeIdeaList);
    }

    const mapSearchResults = (data) => {
        let mappedChangeIdeaList = data.changeIdeaSearchTableItems.map((changeIdea) => {
            let copyOfSearchResults = JSON.parse(JSON.stringify(ideaListForPackage));
            copyOfSearchResults.forEach((packageIdea) => {
                if (packageIdea.changeIdeaExternalReference === changeIdea.changeIdeaExternalReference) {
                    packageIdea.includeInPackage = !changeIdea.includeInPackage;
                }
            });
            return {...changeIdea, includeInPackage: false}
        });
        setSearchResults(mappedChangeIdeaList);
    }

    const handleSelectIdeasButtonClick = async () => {
        const changeIdeaPackage = {
            name: packageName,
        };
        setContext((prevState) => ({
            ...prevState,
            captureNewPackage: true,
            addChangeIdeasToPackage: true,
            changePackageToCreate: changeIdeaPackage,
        }));
        await fetchChangeIdeasByCriteria();
    }

    const fetchChangeIdeasForPackage = async () => {
        setSpinnerText("Retrieving Ideas for Package...");
        let packageExtRef = context.selectedChangePackage.externalReference;
        const [data, error] = await viewChangeIdeasForPackage(context.username, packageExtRef);
        if (data) {
            if (data.responseStatus === "Success") {
                let mappedChangeIdeaForPackageList;
                if (data.changeIdeaSearchResultDTOS) {
                    mappedChangeIdeaForPackageList = data.changeIdeaSearchResultDTOS.map((changeIdea) => {
                        return {...changeIdea, includeInPackage: true}
                    });
                    setIdeaListForPackage(mappedChangeIdeaForPackageList);
                } else {
                    setNoResultsMessage("No Ideas In Package");
                }
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const fetchChangeIdea = async (changeIdeaExternalReference) => {
        let savedChangeIdea = null;
        const [data, error] = await viewChangeIdea(context.username, changeIdeaExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedChangeIdea = data.changeIdeaVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedChangeIdea;
    }

    const handleDeletePackage = async (externalReference) => {
        setSpinnerText("Deleting Package");
        const [data, error] = await deleteChangePackage(context.username, externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                navigateTo("changePackageSearch");
            } else {
                setShowModal(false);
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
            setShowModal(false);
        }
        setSpinnerText(null);
    }

    const handleSaveClick = async () => {
        setSpinnerText("Creating Package");
        const changeIdeaPackageVO = {
            name: packageName,
        };
        let createdPackageExtRef = null;
        const [data, error] = await createChangePackage(context.username, changeIdeaPackageVO);
        if (data) {
            if (data.responseStatus === "Success") {
                createdPackageExtRef = data.createdChangeIdeaPackage.externalReference;
                await updateIdeaListOnCreate(createdPackageExtRef);
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
        setContext((prevState) => ({
            ...prevState,
            activeSection: "Search Change Packages",
        }));
        navigateTo("changePackageSearch");
    }

    const handleUpdateClick = async () => {
        setSpinnerText("Updating Package");
        const changeIdeaPackageVO = {
            name: packageName,
            externalReference: context.selectedChangePackage.externalReference,
        };
        let updatedPackageExtRef = null;
        const [data, error] = await updateChangePackage(context.username, changeIdeaPackageVO);
        if (data) {
            if (data.responseStatus === "Success") {
                updatedPackageExtRef = data.updatedChangeIdeaPackageVO.externalReference;
                await updateIdeaListOnUpdate(updatedPackageExtRef);
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
        navigateTo("changePackageSearch");
    }

    const updateIdeaListOnCreate = async (packageExtRef) => {
        let selectedIdeas = [];
        searchResults.forEach((searchResult) => {
            if (searchResult.includeInPackage) {
                selectedIdeas.push(searchResult);
            }
        });
        await updateSelectedIdeaList(selectedIdeas, packageExtRef);
    }

    const updateIdeaListOnUpdate = async (packageExtRef) => {
        let selectedIdeas = [];
        const copyOfIdeaListForPackage = JSON.parse(JSON.stringify((context.addClick) ? searchResults : ideaListForPackage));
        copyOfIdeaListForPackage.forEach((packageIdea) => {
            if (packageIdea.includeInPackage) {
                packageIdea.changeIdeaPackageExternalReference = packageExtRef;
                selectedIdeas.push(packageIdea);
            } else {
                packageIdea.changeIdeaPackageExternalReference = null;
                selectedIdeas.push(packageIdea);
            }
        });
        await updateSelectedIdeaList(selectedIdeas);
    }

    const updateSelectedIdeaList = async (ideaList, packageExtRef) => {
        for (const changeIdea of ideaList) {
            let savedChangeIdea = await fetchChangeIdea(changeIdea.changeIdeaExternalReference);
            let aimStatementVO = null;
            if (savedChangeIdea) {
                if (savedChangeIdea.to) {
                    aimStatementVO = {
                        toImprove: context.changeIdeaScreen.toImprove,
                        from: context.changeIdeaScreen.from,
                        to: context.changeIdeaScreen.to,
                        by: convertDateToUTC(context.changeIdeaScreen.byDate),
                    }
                } else {
                    aimStatementVO = null;
                }
            }

            if (savedChangeIdea) {
                const changeIdeaVOToUpdate = {
                    title: savedChangeIdea.title,
                    description: savedChangeIdea.description,
                    rationale: savedChangeIdea.rationale,
                    prediction: savedChangeIdea.prediction,
                    concept: savedChangeIdea.concept,
                    visitExternalReference: savedChangeIdea.visitExternalReference,
                    topicAreaExternalReference: savedChangeIdea.topicAreaExternalReference,
                    causeExternalReference: savedChangeIdea.causeExternalReference,
                    causeName: savedChangeIdea.causeName,
                    aimStatementVO: aimStatementVO,
                    processMeasureVOList: savedChangeIdea.processMeasureVOList,
                    observation: savedChangeIdea.observation,
                    priority: savedChangeIdea.priority,
                    changeIdeaFileList: null,
                    changeIdeaPackageExternalReference: (packageExtRef) ? packageExtRef : changeIdea.changeIdeaPackageExternalReference,
                    externalReference: savedChangeIdea.externalReference,
                }
                await updateChangeIdea(context.username, changeIdeaVOToUpdate);
            }
        }
    }

    const handleIdeaRowClick = async (changeIdea) => {
        if (context.captureNewPackage) {
            let copyOfSearchResults = JSON.parse(JSON.stringify(searchResults));
            copyOfSearchResults.forEach((searchResult) => {
                if (searchResult.changeIdeaExternalReference === changeIdea.changeIdeaExternalReference) {
                    searchResult.includeInPackage = !changeIdea.includeInPackage;
                }
            });
            setSearchResults(copyOfSearchResults);
        } else {
            let copyOfSearchResults = JSON.parse(JSON.stringify(ideaListForPackage));
            copyOfSearchResults.forEach((packageIdea) => {
                if (packageIdea.changeIdeaExternalReference === changeIdea.changeIdeaExternalReference) {
                    packageIdea.includeInPackage = !changeIdea.includeInPackage;
                }
            });
            setIdeaListForPackage(copyOfSearchResults);
        }
    }

    const handleAddClick = async () => {
        setContext((prevState) => ({
            ...prevState,
            addChangeIdeasToPackage: true,
            captureNewPackage: true,
            addClick: true,
            changePackageToCreate: context.selectedChangePackage,
            ideaListForPackage: ideaListForPackage,
        }));
        await fetchChangeIdeasByCriteria();
    }

    const handleDeleteModalShow = (extRef, title) => {
        setShowModal(true);
        setExtRefToBeDeleted(extRef);
        setModalTitle(title);
    }

    return (
        <div className={"change-package-screen-container"}>
            {(context.captureNewPackage || !context.captureNewPackage && context.addChangeIdeasToPackage) &&
            <div className={"change-package-name-container"}>
                <label>{"Change Package Name:"}</label>
                <input type="text" value={packageName}
                       onChange={({target}) => {
                           setPackageName(target.value)
                       }}/>
            </div>}
            <div className={"change-package-header-container"}>
                {(context.captureNewPackage || !context.captureNewPackage && context.addChangeIdeasToPackage) &&
                <label>Change Ideas In Package:</label>}
            </div>
            <div className="problem-search-results-container">
                <div className="problem-search-results-table-container">
                    <SelectableTable tableData={(context.captureNewPackage) ? searchResults : ideaListForPackage}
                                     hiddenColumns={["externalReference", "changeIdeaExternalReference", "isDeleted", "created", "lastModified"]}
                                     onRowClick={handleIdeaRowClick}
                                     noResultsMessage={noResultsMessage}/>
                </div>
            </div>
            <div className={"change-package-button-container"}>
                {(!context.addChangeIdeasToPackage) &&
                <StandardButton text={"Select Ideas"}
                                onClick={handleSelectIdeasButtonClick}
                                disabled={!packageName}/>}
                {(context.captureNewPackage && context.addChangeIdeasToPackage && !context.addClick) &&
                <StandardButton text={"Save"}
                                onClick={handleSaveClick}
                                disabled={!packageName}/>}
                {(context.addClick) &&
                <StandardButton text={"Update"}
                                onClick={handleUpdateClick}
                                disabled={!packageName}/>}
                {(!context.captureNewPackage && context.addChangeIdeasToPackage && ideaListForPackage.length > 0) &&
                <StandardButton text={"Update"}
                                onClick={handleUpdateClick} disabled={!packageName}/>}
                {(!context.captureNewPackage && context.addChangeIdeasToPackage) &&
                <StandardButton text={"Add Ideas"}
                                onClick={handleAddClick}/>}
                {(context.addChangeIdeasToPackage && !context.captureNewPackage) &&
                <StandardButton text={"Delete Package"}
                                onClick={() => {
                                    handleDeleteModalShow(context.selectedChangePackage.externalReference, context.selectedChangePackage.name);
                                }}/>}
                {showModal &&
                <ConfirmDeleteModal showModal={showModal}
                                    setShowModal={setShowModal}
                                    titleToDelete={modalTitle}
                                    onDeleteClick={async () => {
                                        await handleDeletePackage(extRefToBeDeleted)
                                    }}/>}
                {(!context.captureNewPackage) &&
                <StandardButton text={"Back"} onClick={navigateBack}/>}
            </div>
        </div>
    );
}
